import { DocumentSourceEnum } from '../document-wizard/document-editor/constants/EditorConstats';

export type TemplatePublishStatus = 'draft' | 'unpublished_changes' | 'published';

export const templatePublishStatusMessages: Record<TemplatePublishStatus, { id: string; defaultMessage: string }> = {
  draft: { id: 'template.publish-status.draft', defaultMessage: 'Draft' },
  unpublished_changes: { id: 'template.publish-status.unpublished-changes', defaultMessage: 'Unpublished changes' },
  published: { id: 'template.publish-status.published', defaultMessage: 'Published' },
};

export const USES_LAST_PUBLISHED_VERSION_MESSAGE =
  "Uses the last published version. Recent edits aren't included until they're published.";

export const DRAFT_SAVED_BANNER_MESSAGE = 'Your edits are saved as a draft.';

export const draftCreatedNotice = (isClause: boolean) =>
  isClause
    ? 'Clause saved as a draft. Someone with template publishing permission needs to publish it before it appears in the clause library.'
    : 'Template saved as a draft. Someone with template publishing permission needs to publish it before it can be used.';

/**
 * Status for the template page, where the published body is loaded. Uploaded
 * PDFs have no draft/publish cycle: the file is the published version.
 */
export const getTemplatePublishStatus = ({
  isUploaded,
  hasPublishedVersion,
  hasPendingChanges,
}: {
  isUploaded: boolean;
  hasPublishedVersion: boolean;
  hasPendingChanges: boolean;
}): TemplatePublishStatus => {
  if (isUploaded) return 'published';
  if (!hasPublishedVersion) return 'draft';
  return hasPendingChanges ? 'unpublished_changes' : 'published';
};

/**
 * Status for list rows, which are fetched without template bodies. A new
 * template is created hidden with pending changes and the first publish
 * un-hides it, so `hidden && hasPendingChanges` stands in for "never
 * published". A deactivated template that also has unpublished edits reads as
 * a draft too; the list data cannot tell the two apart.
 */
export const getListTemplatePublishStatus = (template: {
  source?: string | null;
  hidden?: boolean | null;
  hasPendingChanges?: boolean | null;
}): TemplatePublishStatus => {
  if (template.source === DocumentSourceEnum.IMPORTED) return 'published';
  if (!template.hasPendingChanges) return 'published';
  return template.hidden ? 'draft' : 'unpublished_changes';
};
